import { FC, useCallback, useRef } from 'react';
import * as tracking from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { peopleAlsoBrowsed } from '../../shared/sharedMessages';
import {
    PdpRespProductTileCarousel,
    PdpRespProductTileCarouselProps,
} from './PdpRespProductTileCarousel/PdpRespProductTileCarousel';

// Helpers
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    trackSimilarItemClick,
    trackCarouselItemImpressions,
    PERSONALIZATION_MODULE_ITEMS_LIST,
    trackPersonalizedItemsRendered,
    trackPersonalizedItemsVisible,
} from '../tracking/similarItemsTracking';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

// types
import {
    PdpRespPersonalizedItemsCarousel_viewer$key,
    PdpRespPersonalizedItemsCarousel_viewer$data,
} from './__generated__/PdpRespPersonalizedItemsCarousel_viewer.graphql';
import { PdpRespPersonalizedItemsCarousel_user$key } from './__generated__/PdpRespPersonalizedItemsCarousel_user.graphql';
import { PdpRespProductTileCarousel_items$data } from './PdpRespProductTileCarousel/__generated__/PdpRespProductTileCarousel_items.graphql';

type Props = {
    viewer: PdpRespPersonalizedItemsCarousel_viewer$key;
    user: PdpRespPersonalizedItemsCarousel_user$key | null;
    currency: string;
};

type CarouselItem = NonNullable<NonNullable<PdpRespProductTileCarousel_items$data>[number]>;
type ItemAssociationType = NonNullable<
    NonNullable<
        NonNullable<
            PdpRespPersonalizedItemsCarousel_viewer$data['personalizedItemsList']
        >['associatedItems']
    >[number]
>['associationType'];

const processAssociatedItemForTracking = (
    item: CarouselItem,
    variant: ItemAssociationType,
    isPersonalized: boolean | null
): CarouselItem => {
    const { ecommerceTrackingParams = {}, ...itemParams } = item;
    return {
        ...itemParams,
        ecommerceTrackingParams: {
            ...ecommerceTrackingParams,
            variant,
            dimension125: isPersonalized,
        },
    };
};

const userFragment = graphql`
    fragment PdpRespPersonalizedItemsCarousel_user on User {
        ...PdpRespProductTileCarousel_user
    }
`;

const viewerFragment = graphql`
    fragment PdpRespPersonalizedItemsCarousel_viewer on Viewer {
        ...PdpRespProductTileCarousel_viewer
        personalizedItemsList: personalizedItems(userId: $userId, itemId: $itemId, pageSize: 10) {
            totalPersonalizedResults
            associatedItems {
                associationType
                isPersonalized
                item {
                    ...PdpRespProductTileCarousel_items
                        @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                }
            }
        }
    }
`;

const MIN_PERSONALIZED_ITEMS_COUNT = 5;
const MAX_PERSONALIZED_ITEMS_COUNT = 10;

export const PdpRespPersonalizedItemsCarousel: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const impressionTrackingRef = useRef({});

    const { personalizedItemsList } = viewer || {};

    const associatedItems = personalizedItemsList?.associatedItems || [];
    const personalizedItems = associatedItems
        .map(association =>
            association && association.associationType && association.item
                ? {
                      variant: association?.associationType,
                      isPersonalized: association?.isPersonalized,
                      ...association?.item,
                  }
                : null
        )
        .filter(filterNulls);

    const onItemsVisible = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onItemsVisible']>
    >(
        (visibleItems, startIndex) =>
            trackCarouselItemImpressions({
                visibleItems: visibleItems?.map((visibleItem, index) =>
                    processAssociatedItemForTracking(
                        visibleItem,
                        personalizedItems?.[startIndex + index]?.variant,
                        personalizedItems?.[startIndex + index]?.isPersonalized
                    )
                ),
                startIndex,
                impressionTrackingRef,
                list: PERSONALIZATION_MODULE_ITEMS_LIST,
            }),
        [personalizedItems]
    );

    const onClickItemTile = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onClickItemTile']>
    >(
        (tileItem, index) => {
            trackSimilarItemClick(
                index,
                processAssociatedItemForTracking(
                    tileItem,
                    personalizedItems?.[index]?.variant,
                    personalizedItems?.[index]?.isPersonalized
                ),
                PERSONALIZATION_MODULE_ITEMS_LIST
            );
        },
        [personalizedItems]
    );

    if (personalizedItems.length < MIN_PERSONALIZED_ITEMS_COUNT) {
        return null;
    }

    return (
        <PdpRespProductTileCarousel
            viewer={viewer}
            items={
                personalizedItems.length !== MAX_PERSONALIZED_ITEMS_COUNT
                    ? personalizedItems.slice(0, MIN_PERSONALIZED_ITEMS_COUNT)
                    : personalizedItems
            }
            user={user}
            currency={currency}
            title={peopleAlsoBrowsed}
            location={locations.PERSONALIZED_ITEM_LIST}
            onCarouselRendered={() =>
                trackPersonalizedItemsRendered(
                    personalizedItems.length,
                    personalizedItemsList?.totalPersonalizedResults
                )
            }
            onClickItemTile={onClickItemTile}
            onItemsVisible={onItemsVisible}
            onClickArrow={() => {
                tracking.trackEvent(
                    {
                        category: 'product',
                        isInteractiveEvent: true,
                        action: 'pdp module arrow click',
                        label: PERSONALIZATION_MODULE_ITEMS_LIST,
                    },
                    null
                );
            }}
            dataTn="pdp-people-also-browsed-grid"
            onCarouselVisible={trackPersonalizedItemsVisible}
            onCarouselReset={() => {}}
        />
    );
};

export default PdpRespPersonalizedItemsCarousel;
